// ABOUTME: Pure functions for quantity validation and stock calculations
// ABOUTME: Used by product model and cart feature for consistent validation

import { z } from "zod";

// ============================================================================
// Constants
// ============================================================================

export const QUANTITY_LIMITS = {
  MIN: 1,
  MAX: 1000000,
  DEFAULT: 1,
} as const;

export const STOCK_THRESHOLDS = {
  LOW_STOCK: 10,
  OUT_OF_STOCK: 0,
} as const;

// ============================================================================
// Zod Schemas
// ============================================================================

export const quantitySchema = z
  .number({
    message: "Must be a number",
  })
  .int("Must be an integer")
  .positive("Must be positive")
  .max(QUANTITY_LIMITS.MAX, "Too large");

export const amountSchema = z
  .number({
    message: "Must be a number",
  })
  .positive("Must be positive")
  .max(100000000, "Too large");

// ============================================================================
// Stock Calculations
// ============================================================================

/**
 * Calculate maximum available quantity considering stock and cart
 */
export function calculateMaxAvailableQuantity(
  stockQuantity: number,
  cartQuantity: number = 0,
): number {
  return Math.max(0, stockQuantity - cartQuantity);
}

/**
 * Calculate total price for given quantity
 */
export function calculateTotalPrice(
  priceCents: number,
  quantity: number,
): number {
  return priceCents * quantity;
}

/**
 * Check if product is low stock
 */
export function isLowStock(quantity: number): boolean {
  return quantity > 0 && quantity <= STOCK_THRESHOLDS.LOW_STOCK;
}

/**
 * Check if product is out of stock
 */
export function isOutOfStock(quantity: number): boolean {
  return quantity === STOCK_THRESHOLDS.OUT_OF_STOCK;
}

/**
 * Get stock status text
 */
export function getStockStatus(quantity: number): {
  status: "out_of_stock" | "low_stock" | "in_stock";
  text: string;
  colorClass: string;
} {
  if (quantity === 0) {
    return {
      status: "out_of_stock",
      text: "Нет в наличии",
      colorClass: "text-destructive",
    };
  }
  if (quantity <= 10) {
    return {
      status: "low_stock",
      text: `${quantity} в наличии`,
      colorClass: "text-warning",
    };
  }
  return {
    status: "in_stock",
    text: `${quantity} в наличии`,
    colorClass: "text-foreground",
  };
}

// ============================================================================
// Quantity Validation
// ============================================================================

/**
 * Validate if a quantity is within allowed limits
 */
export function isValidQuantity(
  quantity: number,
  maxAvailable?: number,
): boolean {
  if (quantity < QUANTITY_LIMITS.MIN) return false;
  if (quantity > QUANTITY_LIMITS.MAX) return false;
  if (maxAvailable !== undefined && quantity > maxAvailable) return false;
  return true;
}

/**
 * Clamp quantity to valid range
 */
export function clampQuantity(quantity: number, maxAvailable?: number): number {
  let clamped = Math.max(QUANTITY_LIMITS.MIN, quantity);
  clamped = Math.min(QUANTITY_LIMITS.MAX, clamped);

  if (maxAvailable !== undefined) {
    clamped = Math.min(maxAvailable, clamped);
  }

  return Math.floor(clamped);
}

/**
 * Validate quantity and return result
 */
export function validateQuantity(
  quantity: number,
  maxAvailable?: number,
): {
  isValid: boolean;
  clampedQuantity: number;
  error?: string;
} {
  const result = quantitySchema.safeParse(quantity);

  if (!result.success) {
    return {
      isValid: false,
      clampedQuantity: QUANTITY_LIMITS.DEFAULT,
      error: result.error.issues[0]?.message,
    };
  }

  const clamped = clampQuantity(quantity, maxAvailable);

  return {
    isValid: true,
    clampedQuantity: clamped,
  };
}
